import React from "react";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import { QK } from "../../../base/qk";
import { getClientWeeklyTimeline } from "../../../api/services/TimeLine/timeLine";

const ClientWeeklyTimelineView = ({ clientId, selectedDate, locationId }) => {
  const timelineStart = moment(selectedDate).startOf("isoWeek");
  const columns = Array.from({ length: 7 }, (_, i) => i);

  const { data: timelineResponse, isLoading } = useQuery(
    [QK.clientsWeeklyTimeline, clientId, selectedDate, locationId],
    () => getClientWeeklyTimeline(clientId || "", selectedDate, locationId || "all"),
    { enabled: !!selectedDate, refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <Box sx={{ height: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} thickness={4} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "calc(100vh - 140px)", // উচ্চতা বাড়ানো হয়েছে
        borderRadius: "8px",
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ minWidth: "1100px" }}>
          
          {/* HEADER: স্লিমার হেডার */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 100,
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #cbd5e1",
            }}
          >
            <Box
              sx={{
                width: 150, // উইডথ কমানো হয়েছে
                px: 1.5,
                py: 1,
                position: "sticky",
                left: 0,
                zIndex: 110,
                bgcolor: "#f8fafc",
                borderRight: "1px solid #cbd5e1",
              }}
            >
              <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#64748b" }}>
                SERVICE USER
              </Typography>
            </Box>

            {columns.map((col) => {
              const dateObj = moment(timelineStart).add(col, "days");
              const isToday = dateObj.isSame(moment(), "day");

              return (
                <Box
                  key={col}
                  sx={{
                    flex: 1,
                    py: 0.5,
                    textAlign: "center",
                    borderRight: "1px solid #e2e8f0",
                    bgcolor: isToday ? "#eff6ff" : "transparent",
                  }}
                >
                  <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#64748b", lineHeight: 1 }}>
                    {dateObj.format("ddd").toUpperCase()}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#0f172a" }}>
                    {dateObj.format("DD MMM")}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ROWS: টাইট স্পেসিং */}
          {timelineResponse?.map((client) => {
            return (
              <Box
                key={client.client_id}
                sx={{
                  display: "flex",
                  minHeight: 45, // রো এর উচ্চতা অনেক কমানো হয়েছে
                  borderBottom: "1px solid #f1f5f9",
                  "&:hover": { bgcolor: "#fcfcfc" },
                }}
              >
                {/* LEFT CELL */}
                <Box
                  sx={{
                    width: 150,
                    px: 1.5,
                    py: 0.5,
                    position: "sticky",
                    left: 0,
                    zIndex: 50,
                    bgcolor: "white",
                    borderRight: "1px solid #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Avatar sx={{ width: 22, height: 22, fontSize: "9px", bgcolor: "#334155" }}>
                    {client.client_name?.[0]}
                  </Avatar>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#334155",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {client.client_name}
                  </Typography>
                </Box>

                {/* DAY CELLS */}
                <Box sx={{ flex: 1, display: "flex" }}>
                  {client.data?.map((day, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        flex: 1,
                        px: 0.4,
                        py: 0.4,
                        borderRight: "1px solid #f8fafc",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.3, // টাস্কগুলোর মাঝে খুব কম গ্যাপ
                      }}
                    >
                      {day.tasks?.map((task) => {
                        const color = task.display_color || "#6366f1";

                        return (
                          <Tooltip
                            key={task.id}
                            arrow
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{task.visit_name}</Typography>
                                <Typography sx={{ fontSize: '10px' }}>{task.scheduled} - {task.end_time}</Typography>
                                <Typography sx={{ fontSize: '10px' }}>Staff: {task.assigned_staff || 'N/A'}</Typography>
                              </Box>
                            }
                          >
                            <Box
                              sx={{
                                px: 0.5,
                                py: 0.2,
                                borderRadius: "3px",
                                backgroundColor: `${color}15`,
                                borderLeft: `3px solid ${color}`,
                                cursor: "pointer",
                                "&:hover": { backgroundColor: `${color}25` },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "10.5px", // ফন্ট ক্লিয়ার রাখার জন্য ১০.৫ রাখা হয়েছে
                                  fontWeight: 900,
                                  color: "#1e293b",
                                  textAlign: 'center',
                                  lineHeight: 1.2
                                }}
                              >
                                {task.scheduled}-{task.end_time}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientWeeklyTimelineView;